import { Component, Input, OnInit, inject } from "@angular/core";
import { PriorityStore } from "@app/services/store/priority-store";
import { Store as NgrxStore } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { Store } from "@bryntum/core-thin";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ColorChooserComponent } from "@app/view/shared/color-chooser/color-chooser.component";
import { Priority } from "@app/models/rest/priority";
import { AutoBookProfileStore } from "@app/services/store/auto-book-profile-store";
import { BookPastDueBehaviour } from "@app/models/enums/book-past-due-behaviour";
import { Column, Model } from "@bryntum/engine-thin";
import { MatToolbar } from "@angular/material/toolbar";
import { MatButton } from "@angular/material/button";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
  selector: "app-priority-list",
  templateUrl: "./priority-list.component.html",
  styleUrls: ["./priority-list.component.scss"],
  imports: [MatToolbar, MatButton, GridComponent, MatPaginator, TranslatePipe]
})
export class PriorityListComponent
  extends BaseDataTablePageDirective<Priority>
  implements OnInit
{
  priorityStore: PriorityStore;
  protected autoBookProfileStore = inject(AutoBookProfileStore);
  protected store: NgrxStore<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;

  public cyclicalProfileListStore = new Store({
    data: []
  });
  public bookPastDueBehaviourStore = new Store({
    data: []
  });
  @Input()
  public columns: object[] = [
    {
      type: "template",
      cellMenuItems: [
        {
          text: "Edit Colour Scheme",
          icon: "b-fa b-fa-palette",
          onItem: this.colorChooser.bind(this)
        }
      ],
      id: "colour-column",
      template: ({ record }) =>
        `<div style="
background-color: ${record.get("backgroundColor")};
color: ${record.get("foregroundColor")};
position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-content: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    cursor: pointer;
">Preview</div>`
    },

    {
      field: "code",
      text: "Code",
      flex: 1,
      editor: true
    },
    {
      field: "description",
      text: "Description",
      flex: 3,
      editor: true
    },
    {
      field: "scoreWeight",
      text: "Score Weight",
      flex: 1,
      editor: true,
      type: "number"
    },
    {
      field: "type",
      text: "Type",
      flex: 1,
      editor: {
        type: "dropdown",
        items: ["Normal", "High", "Emergency", "Cyclical"]
      }
    },
    {
      text: "Auto-Book Profile",
      field: "autoBookProfile",
      flex: 1,
      renderer: ({ record }) => record.autoBookProfile?.description ?? "None",
      editable: true,
      cellMenuItems: [
        {
          text: "Clear Auto-Book Profile",
          icon: "b-fa b-fa-times",
          onItem: ({ record }) => (record.autoBookProfile = null)
        }
      ],
      editor: {
        type: "dropdown",
        store: this.cyclicalProfileListStore,
        displayField: "description",
        valueField: null
      }
    },
    {
      field: "bookBeforeDueBehaviour",
      text: "Book Before Planning Window",
      flex: 1,
      editor: {
        type: "dropdown",
        store: this.bookPastDueBehaviourStore,
        displayField: "name",
        valueField: "value"
      }
    },
    {
      field: "bookPastDueBehaviour",
      text: "Book Past Due",
      flex: 1,
      editor: {
        type: "dropdown",
        store: this.bookPastDueBehaviourStore,
        displayField: "name",
        valueField: "value"
      }

      // renderer : ({ record }) =>  record.bookPastDueBehaviour ? record.bookPastDueBehaviour : "System Default"
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const priorityStore = inject(PriorityStore);
    const store = inject<NgrxStore<any>>(NgrxStore);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      priorityStore,
      store,
      dialog,
      "TicketCompletionCode",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );
    this.priorityStore = priorityStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;

    // Bind colorChooser method to ensure correct context
    this.colorChooser = this.colorChooser.bind(this);
    this.onCellClick = this.onCellClick.bind(this);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  colorChooser(data: { record: Priority }) {
    console.log("item clicked", data);
    let dialogRef = this.dialog.open(ColorChooserComponent, {
      data: {
        currentBackground: data.record.backgroundColor,
        currentForeground: data.record.foregroundColor
      }
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (value.success) {
        data.record.backgroundColor = value.backgroundColor;
        data.record.foregroundColor = value.foregroundColor;
      }
    });
  }

  ngOnInit() {
    super.ngOnInit();

    // Add "None" option for autobook profile dropdown - special null value
    const noneOption = { description: "None", isNoneOption: true };
    this.cyclicalProfileListStore.add(noneOption);
    this.autoBookProfileStore.loadAll().then((value) => {
      console.log("Adding auto-book profile: ", value);
      this.cyclicalProfileListStore.add(value.resources);
    });

    this.gridStore.autoCommit = false;
    this.gridStore.load({});
    //

    this.bookPastDueBehaviourStore.add({
      name: "System Default",
      value: "DEFAULT"
    });
    this.bookPastDueBehaviourStore.add({ name: "Allow", value: "ALLOW" });
    this.bookPastDueBehaviourStore.add({ name: "Deny", value: "DENY" });
  }

  onCellClick(event: { record: Model; column: Column }) {
    if (event.column.id === "colour-column") {
      this.colorChooser({ record: event.record });
    }
  }

  addRow() {
    this.gridStore.insert(0, {
      code: "",
      description: "",
      backgroundColor: "#FFFFFF",
      foregroundColor: "#000000",
      scoreWeight: 1,
      type: "Normal",
      bookPastDueBehaviour: BookPastDueBehaviour.DEFAULT
    });
  }
}
